import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "./ui/button";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";

const Header = ({ hideBookButton }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const showLoginButton = location.pathname !== "/login";

  const navItems = [
    { label: "Home", target: "home" },
    { label: "About", target: "about" },
    { label: "Services", target: "services" },
    { label: "Our Specialists", target: "team" },
    { label: "Gallery", link: "/gallery" },
    { label: "Locations", target: "branches" },
    { label: "Contact", target: "contact" },
    { label: "Careers", link: "/careers" },
  ];

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) setUser(JSON.parse(storedUser));
  }, []);

  const getFirstName = () => (user?.name ? user.name.split(" ")[0] : "Guest");

  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    navigate("/login");
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".user-menu")) setDesktopMenuOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
 <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-lg border-b border-slate-200">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-4">
    <div className="flex justify-between items-center h-16 w-full">
      {/* Logo + Title */}
      <div
        className="flex items-center space-x-3 cursor-pointer"
        onClick={() => {
          if (location.pathname === "/") {
            // Already on home page → scroll to top
            window.scrollTo({ top: 0, behavior: "smooth" });
          } else {
            // Navigate to home page
            navigate("/");
          }
        }}
      >
        <img
  src="./VPJCLogo-New.png"
  alt="Clinic Logo"
  className="h-11 w-11 sm:h-12 sm:w-12 rounded-full transform transition-transform duration-300 hover:scale-105"
/>


  <div className="flex flex-col leading-tight cursor-pointer group">
  <span className="text-xs sm:text-sm md:text-base font-bold text-[#204080] ">
    Visakha Physiotherapy
  </span>
  <span className="text-xs sm:text-sm md:text-base font-bold text-[#204080] ">
    & Jasti Chiropractic
  </span>
</div>




      </div>





          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-4">
            {navItems.map((item) =>
              item.link ? (
                <Link
                  key={item.label}
                  to={item.link}
                  className="relative text-gray-700 font-medium tracking-wide text-[15px] hover:text-[#204080] transition-colors duration-300 group"
                >
                  {item.label}
                  <span className="absolute left-0 -bottom-1 w-0 h-[2px] bg-[#204080] transition-all duration-300 group-hover:w-full"></span>
                </Link>
              ) : (
                <button
                  key={item.target}
                  onClick={() => {
                    if (location.pathname === "/") {
                      const section = document.getElementById(item.target);
                      if (section)
                        section.scrollIntoView({ behavior: "smooth" });
                    } else {
                      navigate("/", { state: { scrollTo: item.target } });
                    }
                  }}
                  className="relative text-gray-700 font-medium tracking-wide text-[15px] hover:text-[#204080] transition-colors duration-300 group"
                >
                  {item.label}
                  <span className="absolute left-0 -bottom-1 w-0 h-[2px] bg-[#204080] transition-all duration-300 group-hover:w-full"></span>
                </button>
              )
            )}
          </nav>

          {/* Right Buttons */}
          {/* Right Side: Tablet & Desktop */}
          <div className="hidden lg:flex items-center space-x-3 md:space-x-4">
            {/* Desktop buttons (unchanged) */}
            {!hideBookButton && (
              <>
                {user ? (
                  <div className="relative user-menu">
                    <button
                      onClick={() => setDesktopMenuOpen(!desktopMenuOpen)}
                      className="px-4 py-2 rounded-lg bg-[#204080] text-white font-semibold hover:bg-[#2E5AAC] transition-all"
                    >
                      Hey {getFirstName()}!
                    </button>
                    {desktopMenuOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                        <ul className="py-2 text-sm text-gray-700">
                          {(user.role === "patient" ||
                            user.role === "guest") && (
                            <li>
                              <Link
                                to="/patient-dashboard"
                                className="block px-4 py-2 hover:bg-gray-100"
                                onClick={() => setDesktopMenuOpen(false)}
                              >
                                My Appointments
                              </Link>
                            </li>
                          )}
                          {(user.role === "doctor" ||
                            user.role === "admin") && (
                            <li>
                              <Link
                                to={`/${user.role}-dashboard`}
                                className="block px-4 py-2 hover:bg-gray-100"
                                onClick={() => setDesktopMenuOpen(false)}
                              >
                                View Appointments
                              </Link>
                            </li>
                          )}
                          <li>
                            <button
                              onClick={() => setShowLogoutConfirm(true)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600"
                            >
                              Logout
                            </button>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  showLoginButton && (
                    <Link to="/login">
                      <Button
                        variant="outline"
                        className="text-[#204080] border-[#204080] hover:bg-[#204080] hover:text-white px-4 py-2 rounded-lg transition-all duration-300"
                      >
                        Login
                      </Button>
                    </Link>
                  )
                )}
                <Link to="/book-appointment">
                  <Button className="bg-[#204080] hover:bg-[#2E5AAC] text-white px-6 py-2 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap">
                    Book Appointment
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Tablet & Mobile Menu Button */}
          <div className="flex items-center lg:hidden space-x-2">
            {/* Show Book Appointment in Tablet (sm to md) */}

            {/* Hamburger (mobile + tablet) */}
            <button
  onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-[#204080] transition-colors duration-300"
>
  {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
</button>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Navigation */}
      {/* Mobile/Tablet Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t">
          <div className="px-2 pt-2 pb-4 space-y-2">
            {navItems.map((item) =>
              item.link ? (
                <Link
                  key={item.label}
                  to={item.link}
                  className="block px-3 py-2 text-gray-700 hover:text-[#204080] transition-colors duration-300"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.target}
                  onClick={() => {
                    if (location.pathname === "/") {
                      const section = document.getElementById(item.target);
                      if (section)
                        section.scrollIntoView({ behavior: "smooth" });
                    } else {
                      navigate("/", { state: { scrollTo: item.target } });
                    }
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 text-gray-700 hover:text-[#204080] transition-colors duration-300"
                >
                  {item.label}
                </button>
              )
            )}

            {/* Mobile User + Book Section */}
            {!hideBookButton && (
              <div className="mt-3 flex flex-col space-y-3 border-t pt-3">
                {!user && showLoginButton && (
                  <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                    <Button
                      variant="outline"
                      className="w-full justify-center text-[#204080] border-[#204080] hover:bg-[#204080] hover:text-white"
                    >
                      Login
                    </Button>
                  </Link>
                )}
                {user && (
                  <div className="relative user-menu w-full flex justify-center">
                    <button
                      onClick={() => setDesktopMenuOpen(!desktopMenuOpen)}
                      className="px-4 py-2 rounded-lg text-[#204080] font-semibold hover:bg-[#2E5AAC] transition-all w-full"
                    >
                      Hi! {getFirstName()}
                    </button>
                    {desktopMenuOpen && (
                      <div className="absolute top-12 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                        <ul className="py-2 text-sm text-gray-700">
                          {(user.role === "patient" ||
                            user.role === "guest") && (
                            <li>
                              <Link
                                to="/patient-dashboard"
                                className="block px-4 py-2 hover:bg-gray-100"
                                onClick={() => {
                                  setDesktopMenuOpen(false);
                                  setIsMenuOpen(false);
                                }}
                              >
                                My Appointments
                              </Link>
                            </li>
                          )}
                          {(user.role === "doctor" ||
                            user.role === "admin") && (
                            <li>
                              <Link
                                to={`/${user.role}-dashboard`}
                                className="block px-4 py-2 hover:bg-gray-100"
                                onClick={() => setDesktopMenuOpen(false)}
                              >
                                View Appointments
                              </Link>
                            </li>
                          )}
                          <li>
                            <button
                              onClick={() => setShowLogoutConfirm(true)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600"
                            >
                              Logout
                            </button>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                <Link
                  to="/book-appointment"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Button className="w-full bg-[#204080] hover:bg-[#2E5AAC] text-white justify-center">
                    Book Appointment
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Logout Confirmation */}
      <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Logout</DialogTitle>
            <DialogDescription>
              Are you sure you want to log out of your account?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setShowLogoutConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={() => {
                handleLogout();
                setShowLogoutConfirm(false);
              }}
            >
              Logout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Header;